import { X } from "lucide-react";

import type { PlutoWaitingPrompt } from "./pluto-session";

type PlutoWaitingPromptsProps = {
  prompts: readonly PlutoWaitingPrompt[];
  /** Whether the last turn failed, which holds the queue until it is read. */
  isHeld?: boolean;
  onRemove: (id: string) => void;
};

/**
 * The questions asked while Pluto was working.
 *
 * They sit above the composer rather than in the transcript, because they have
 * not been asked yet: showing them as turns would put a question in the
 * conversation that no answer will ever follow if the reader takes it back.
 */
export const PlutoWaitingPrompts = ({ prompts, isHeld = false, onRemove }: PlutoWaitingPromptsProps) => {
  if (prompts.length === 0) return null;

  return (
    <ul className="mb-3 space-y-1.5">
      {isHeld ? (
        <li className="text-xs text-stone-500">
          Waiting until the last question is sorted out — ask again to send it now.
        </li>
      ) : null}
      {prompts.map((prompt) => (
        <li
          className="flex items-start gap-2 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-3 py-2"
          key={prompt.id}
        >
          <span className="mt-0.5 shrink-0 text-[11px] font-medium uppercase tracking-wide text-stone-500">Next</span>
          <span className="min-w-0 flex-1 truncate text-sm text-stone-600">{prompt.text}</span>
          <button
            aria-label={`Remove queued question: ${prompt.text}`}
            className="shrink-0 rounded p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-700"
            type="button"
            onClick={() => onRemove(prompt.id)}
          >
            <X aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
};
